import React, { useRef } from 'react';
import { Box, ButtonBase } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

export interface SegmentedOption<T> {
  value: T;
  label: string;
}

interface SegmentedControlProps<T> {
  id?: string;
  options: SegmentedOption<T>[];
  value: T | null;
  onChange: (value: T) => void;
  /** Points at the visible label; a radiogroup is not reached by a label's htmlFor. */
  labelledBy?: string;
  ariaLabel?: string;
  describedBy?: string;
  invalid?: boolean;
  width?: number | string;
  onBlur?: () => void;
  disabled?: boolean;
  /** Permanently fixed: disabled, with a lock at the end. */
  locked?: boolean;
}

/**
 * One choice out of a few, all of them visible - the style of the payment method in the
 * payment panel.
 *
 * A radiogroup rather than toggle buttons: exactly one is always the answer, and arrow
 * keys move between the choices as they do in any radio group.
 */
function SegmentedControl<T extends string | number | boolean>({
  id,
  options,
  value,
  onChange,
  labelledBy,
  ariaLabel,
  describedBy,
  invalid,
  width,
  onBlur,
  disabled: disabledProp,
  locked
}: SegmentedControlProps<T>) {
  const disabled = !!disabledProp || !!locked;
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const selectedIndex = options.findIndex(option => option.value === value);

  const move = (from: number, delta: number) => {
    const next = (from + delta + options.length) % options.length;
    onChange(options[next].value);
    refs.current[next]?.focus();
  };

  return (
    <Box
      id={id}
      role="radiogroup"
      aria-labelledby={labelledBy}
      aria-label={ariaLabel}
      aria-describedby={describedBy}
      aria-invalid={invalid || undefined}
      aria-disabled={disabled || undefined}
      onBlur={event => {
        if (onBlur && !event.currentTarget.contains(event.relatedTarget as Node | null)) onBlur();
      }}
      sx={{
        display: 'flex',
        width: width ?? '100%',
        maxWidth: '100%',
        bgcolor: 'surface.tint',
        p: 0.5,
        borderRadius: '10px',
        gap: 0.5,
        alignItems: 'center',
        ...(invalid ? { boxShadow: theme => `inset 0 0 0 1px ${theme.palette.error.main}` } : {}),
        // the same dashed outline as a disabled field; an outline, so nothing moves
        ...(disabled ? { bgcolor: 'surface.hover', outline: 1, outlineStyle: 'dashed', outlineColor: 'field.disabled', outlineOffset: '-1px', cursor: 'not-allowed' } : {})
      }}
    >
      {options.map((option, index) => {
        const selected = option.value === value;
        return (
          <ButtonBase
            key={String(option.value)}
            ref={element => {
              refs.current[index] = element;
            }}
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            // one tab stop for the group: the selected choice, or the first when none is
            tabIndex={selected || (selectedIndex === -1 && index === 0) ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={event => {
              if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                event.preventDefault();
                move(index, 1);
              } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                event.preventDefault();
                move(index, -1);
              }
            }}
            sx={{
              flex: 1,
              height: 36,
              px: 1.5,
              borderRadius: '8px',
              fontSize: 14,
              whiteSpace: 'nowrap',
              color: selected ? 'primary.main' : 'text.secondary',
              fontWeight: selected ? 600 : 400,
              bgcolor: selected ? 'surface.main' : 'transparent',
              boxShadow: selected ? 1 : 'none',
              '&.Mui-disabled': { color: selected ? 'text.secondary' : 'text.disabled', cursor: 'not-allowed' },
              '&.Mui-focusVisible': { outline: 2, outlineColor: 'primary.main', outlineStyle: 'solid' }
            }}
          >
            {option.label}
          </ButtonBase>
        );
      })}
      {locked && <LockOutlinedIcon aria-hidden sx={{ fontSize: 18, color: 'text.placeholder', mx: 1, flexShrink: 0 }} />}
    </Box>
  );
}

export default SegmentedControl;
